import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { QueryKey } from '../constants/index';

/**
 * Invalidates a fixed set of query keys, through a callback that keeps its identity.
 *
 * It used to return a fresh function on every render, which is harmless in a click handler and
 * quietly destructive in an effect: anything listing it as a dependency re-ran on every render of
 * its owner. The websocket provider was one, so the connection it opens was torn down and made
 * again continuously, and a push that arrived in one of those gaps was never delivered — which
 * read as a change in a data room reaching the other tab only after a reload.
 *
 * The keys are read through a ref so the callback can stay stable without spreading a list of
 * unknown length into the dependencies; they come from the call site as literals and never change
 * for a given caller anyway.
 */
export const useInvalidateQueries = (...keys: QueryKey[]) => {
  const queryClient = useQueryClient();
  const keysRef = useRef(keys);

  keysRef.current = keys;

  const invalidateQuery = useCallback(() => {
    for (const key of keysRef.current) {
      queryClient.invalidateQueries({ exact: false, queryKey: [key] });
    }
  }, [queryClient]);

  return { invalidateQuery };
};
